import { WalletIcon } from "@src/assets";
import {
  Banner,
  Button,
  ButtonSizes,
  ButtonVariants,
  ChainPickerCard,
  ExpandableFees,
  SafeInput,
  Spinner,
  TextInput,
} from "@src/components";
import { CASH_TOKEN_ADDRESS_BY_CHAIN, NUMBER_INPUT_REGEX } from "@src/config";
import { Chain, TokenWithChain } from "@src/models";
import { ethers } from "ethers";
import { useMemo } from "react";
import { GasFees } from "..";
import { humanReadableToWei, weiToHumanReadable } from "@src/utils";
import { useTokenBalance } from "@src/hooks";
import { useSwapContext } from "@src/contexts";
import { PickerType, TokenPanel } from "@src/features";

interface Props {
  amount: string;
  setAmount: React.Dispatch<React.SetStateAction<string>>;
  receiverAddress: string;
  setReceiverAddress: React.Dispatch<React.SetStateAction<string>>;
  onTransfer: () => void;
  chain?: Chain;
  token?: TokenWithChain;
  cashToken?: TokenWithChain;
  areFeesLoading: boolean;
  gasFees?: GasFees;
  notEnoughCashForFees: boolean;
  feesEstimationError: boolean;
}

export const InitTransferView = ({
  amount,
  setAmount,
  receiverAddress,
  setReceiverAddress,
  onTransfer,
  token,
  cashToken,
  areFeesLoading,
  gasFees,
  notEnoughCashForFees,
  feesEstimationError,
}: Props) => {
  const { isMultichain, chain } = useSwapContext();
  const transferTokenBalances = useTokenBalance({ token });
  const cashBalance = useTokenBalance({ token: cashToken });
  const isAddressValid = useMemo(() => {
    if (receiverAddress.length === 0) return true;
    return ethers.utils.isAddress(receiverAddress);
  }, [receiverAddress]);

  const isAmountValid = useMemo(() => {
    if (!transferTokenBalances || !token) return true;

    return (
      BigInt(humanReadableToWei({ amount, decimals: token.decimals })) <=
      BigInt(transferTokenBalances.wei)
    );
  }, [token, amount, transferTokenBalances]);

  const buttonDisabled = useMemo(() => {
    return (
      Number(amount) === 0 ||
      amount.length === 0 ||
      !isAmountValid ||
      receiverAddress.length === 0 ||
      !isAddressValid ||
      notEnoughCashForFees ||
      areFeesLoading ||
      feesEstimationError
    );
  }, [
    amount,
    receiverAddress,
    isAmountValid,
    isAddressValid,
    notEnoughCashForFees,
    areFeesLoading,
    feesEstimationError,
  ]);

  const maxTransferrableAmount = useMemo(() => {
    let amountToTransfer = cashBalance.humanReadable;
    if (token && token.address === CASH_TOKEN_ADDRESS_BY_CHAIN[token.chainId]) {
      amountToTransfer = weiToHumanReadable({
        amount: (
          BigInt(cashBalance.wei) - BigInt(gasFees?.totalFee || "0")
        ).toString(),
        decimals: token?.decimals || 6,
        precisionFractionalPlaces: 2,
      });
      if (Number(amountToTransfer) < 0) {
        amountToTransfer = "0";
      }
    }
    return amountToTransfer;
  }, [cashBalance, token, gasFees]);

  return (
    <div className="flex flex-col gap-2 h-full">
      {isMultichain && (
        <ChainPickerCard chain={chain} shouldUseTransferToken={true} />
      )}
      <div
        className={`flex rounded-[12px] h-[70px] ${
          isAmountValid ? "bg-card_bg" : "bg-error_color/10"
        }`}
      >
        <div className={`flex items-center justify-between gap-2 w-full px-4`}>
          <div className="flex flex-1">
            <SafeInput
              id="transfer-amount-input"
              className={`bg-transparent border-none p-0 text-2xl font-bold placeholder:text-2xl placeholder:lh placeholder:text-text_primary placeholder:text-opacity-60 outline-0 w-full`}
              regex={NUMBER_INPUT_REGEX(token?.decimals || 18)}
              value={amount}
              onChange={setAmount}
              placeholder={"0"}
            />
          </div>
          <TokenPanel
            token={token}
            onMaxClick={(balance) => setAmount(balance)}
            pickerType={PickerType.Transfer}
          />
        </div>
      </div>
      <div
        className={`flex ${
          isAddressValid ? "bg-card_bg" : "bg-error_color/10"
        } rounded-[12px] px-4`}
      >
        <TextInput
          className="h-10"
          inputClassName="font-bold placeholder:font-medium"
          inputIcon={<WalletIcon />}
          placeholder="Enter receiver wallet address"
          value={receiverAddress}
          handleChange={setReceiverAddress}
        />
      </div>
      <ExpandableFees
        gasFees={{
          platformFee: weiToHumanReadable({
            amount: gasFees?.platformFee || "0",
            decimals: cashToken?.decimals || 6,
            precisionFractionalPlaces: 2,
          }),
          networkFee: weiToHumanReadable({
            amount: gasFees?.networkFee || "0",
            decimals: cashToken?.decimals || 6,
            precisionFractionalPlaces: 2,
          }),
          onrampFee: "0",
        }}
        isFetchingRoute={areFeesLoading}
        title={
          <div className="flex items-center justify-between gap-2 w-full">
            <div>{`You receive: ${
              notEnoughCashForFees ? "-" : `${amount || 0} ${token?.symbol}`
            }`}</div>
            <div className="flex gap-1 items-center">
              <div>Fee:</div>
              <div>
                {areFeesLoading ? (
                  <Spinner className="w-[14px] h-[14px]" />
                ) : gasFees ? (
                  weiToHumanReadable({
                    amount: gasFees.totalFee,
                    decimals: cashToken?.decimals || 6,
                    precisionFractionalPlaces: 2,
                  })
                ) : (
                  "-"
                )}
              </div>
              <div>$</div>
            </div>
          </div>
        }
        feesErrorText={`You don't have enough cash to pay fees. ${
          token &&
          token.address === CASH_TOKEN_ADDRESS_BY_CHAIN[token.chainId] &&
          `Max transferrable amount: ${maxTransferrableAmount}`
        }`}
        feesErrorVisible={notEnoughCashForFees}
      />

      <div className="flex flex-1" />
      <>
        {isAddressValid && receiverAddress.length > 0 && (
          <Banner type="warning">
            Transfers withdraw funds from this account and are final. Ensure you
            control the recipient wallet and that it supports the selected
            network before proceeding.
          </Banner>
        )}
      </>
      <Button
        variant={ButtonVariants.Primary}
        disabled={buttonDisabled}
        onClick={onTransfer}
        size={ButtonSizes.Large}
      >
        {!isAmountValid ? (
          `Not enough ${token?.symbol || "tokens"}`
        ) : isAddressValid ? (
          areFeesLoading ? (
            <div className="flex justify-center items-center gap-2">
              <div>Loading</div>
              <Spinner className="w-4 h-4 fill-white" />
            </div>
          ) : feesEstimationError ? (
            "Cannot estimate fee"
          ) : (
            "Transfer"
          )
        ) : (
          "Invalid address"
        )}
      </Button>
    </div>
  );
};
